import cn from 'classnames'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { Button } from 'core-components/library/button'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
} from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'

import { useCockpitCardCornerSlot } from './useCockpitCardCornerSlot'

import styles from './CockpitCardCorner.module.scss'

export interface ICockpitCardCornerAction {
  id: string
  /** What the control is called; rendered as given, so a caller keeps its own `Translation`. */
  label: ReactNode
  onClick?: () => void
  disabled?: boolean
  testId?: string
  /** How the lead is drawn: a filled button by default, plain text for a way somewhere else. */
  variant?: 'contained' | 'text'
  /** Why the lead is off, shown on hover; the menu shows it as the item's second line. */
  hint?: ReactNode
}

export interface ICockpitCardCornerMenuItem extends Partial<ICockpitCardCornerAction> {
  id: string
  /** A second line under the label, e.g. the person a reminder would reach. */
  description?: ReactNode
  /** A ready-made row, e.g. a link with an address of its own, drawn instead of a button. */
  content?: ReactNode
}

export interface ICockpitCardCornerDropdown {
  id: string
  /** What the group is called; the acts it holds name themselves on its list. */
  label: ReactNode
  items: ICockpitCardCornerMenuItem[]
  testId?: string
  menuTestId?: string
}

interface ICockpitCardCornerProps {
  /** The one control drawn as a button; everything else is kept in the menu beside it. */
  lead?: ICockpitCardCornerAction
  /** Acts of one kind under one button, e.g. a reminder and the people it can reach. Drawn
   *  between the lead and the menu, so neither of them has to pick one of the group. */
  dropdown?: ICockpitCardCornerDropdown
  menu?: ICockpitCardCornerMenuItem[]
  /** Name of the `...` button, for readers who never see the glyph. */
  moreLabel: string
  menuTestId?: string
}

const CornerLead = ({ lead }: { lead: ICockpitCardCornerAction }) => (
  <Button
    size="sm"
    variant={lead.variant ?? 'contained'}
    className={styles.lead}
    disabled={lead.disabled}
    __testId={lead.testId}
    onClick={lead.onClick}
  >
    {lead.label}
  </Button>
)

interface ICornerMenuProps {
  items: ICockpitCardCornerMenuItem[]
  children: ReactNode
}

/** The list both the `...` button and a dropdown open, so a row reads the same under either. */
const CornerMenu = ({ items, children }: ICornerMenuProps) => (
  <AbsolutePopup
    rootClassName={styles.menu}
    verticalStartPosition={TPopupVertical.Bottom}
    horizontalStartPosition={TPopupHorizontal.Right}
    horizontalDirection={TPopupHorizontal.Left}
    popupContent={({ action }) => (
      <PopPaper shadowPosition="auto" className={styles.paper}>
        <div className={styles.items}>
          {items.map(({ id, label, description, content, onClick, disabled, testId }) =>
            content ? (
              <div key={id} className={styles.row}>
                {content}
              </div>
            ) : (
              <button
                key={id}
                type="button"
                className={cn(styles.item, { [styles.itemDisabled]: disabled })}
                disabled={disabled}
                data-testid={testId}
                onClick={() => {
                  onClick?.()
                  action.close()
                }}
              >
                {/* A row that names who it reaches keeps the act on the first line and the person
                  under it: the reader picks the act, and the name is what confirms the pick. */}
                <span className={styles.itemText}>
                  {label}
                  {description && <span className={styles.itemDescription}>{description}</span>}
                </span>
              </button>
            )
          )}
        </div>
      </PopPaper>
    )}
  >
    {children}
  </AbsolutePopup>
)

interface ICockpitCardCornerMenuButtonProps {
  /** What the button says; rendered as given, so a caller keeps its own `Translation`. */
  label: ReactNode
  /** Closes the line after the label, e.g. a count of what the list holds. */
  badge?: ReactNode
  /** Name of the button for a screen reader, where the label alone does not say it. */
  ariaLabel?: string
  items: ICockpitCardCornerMenuItem[]
  testId?: string
}

/**
 * A labelled button of the corner that opens the same list as the `...` button. For a corner drawn
 * by the card itself rather than by a section — the card's own ways to elsewhere, beside whatever
 * the section puts in the slot.
 */
export const CockpitCardCornerMenuButton = ({
  label,
  badge,
  ariaLabel,
  items,
  testId,
}: ICockpitCardCornerMenuButtonProps) => (
  <CornerMenu items={items}>
    <button
      type="button"
      className={styles.menuButton}
      aria-label={ariaLabel}
      aria-haspopup="menu"
      data-testid={testId}
    >
      {label}
      {badge}
    </button>
  </CornerMenu>
)

CockpitCardCornerMenuButton.displayName = 'CockpitCardCornerMenuButton'

/**
 * The top-right corner of an expanded cockpit card: one leading button for what the card asks for,
 * and a menu holding everything else.
 *
 * Every card keeps its controls in the same corner, whatever section fills it — a card with four
 * acts is then no wider than a card with one, and a reader looking for what they may do looks in
 * one place. With nothing to offer it draws nothing at all, rather than an empty menu button.
 *
 * Several acts of one kind stand together in a `dropdown` rather than being split between the lead
 * and the menu: two reminders to two people are one question with two answers, and promoting one
 * of them would say the other matters less.
 */
export const CockpitCardCorner = ({
  lead,
  dropdown,
  menu = [],
  moreLabel,
  menuTestId,
}: ICockpitCardCornerProps) => {
  const container = useCockpitCardCornerSlot()

  if (!container || (!lead && !dropdown && menu.length === 0)) {
    return null
  }

  return createPortal(
    <>
      {lead &&
        (lead.hint ? (
          <Tooltip inline tipContent={() => <span>{lead.hint}</span>}>
            <CornerLead lead={lead} />
          </Tooltip>
        ) : (
          <CornerLead lead={lead} />
        ))}

      {dropdown && dropdown.items.length > 0 && (
        <CornerMenu items={dropdown.items}>
          <button type="button" className={styles.dropdown} data-testid={dropdown.testId}>
            {dropdown.label}
            <i className={cn('kbase kbase-chevron-bottom', styles.caret)} aria-hidden="true" />
          </button>
        </CornerMenu>
      )}

      {menu.length > 0 && (
        <CornerMenu items={menu}>
          <button
            type="button"
            className={styles.more}
            aria-label={moreLabel}
            data-testid={menuTestId}
          >
            <i className="kbase kbase-menu-dots" aria-hidden="true" />
          </button>
        </CornerMenu>
      )}
    </>,
    container
  )
}

CockpitCardCorner.displayName = 'CockpitCardCorner'
